import { esApp } from './plataforma';

const DIALOGO = '[role="dialog"], [role="alertdialog"]';

/**
 * Lo que tarda un diálogo en cambiar de alto: llevan `duration-200`. Un poco más, para recolocar
 * el campo con el diálogo ya en su tamaño final.
 */
const TRAS_LA_TRANSICION_MS = 250;

/**
 * Decirle a la hoja de estilos cuánto ocupa el teclado.
 *
 * En iPhone el teclado no encoge la página: se pone encima, y iOS desplaza la vista para enseñar
 * el campo que se edita. Un diálogo alto queda entonces con la cabecera fuera por arriba y los
 * botones bajo el teclado. Los estilos no pueden saber cuánto mide el teclado; aquí se publica en
 * dos variables que usan las reglas de los diálogos en `index.css`:
 *
 * - `--teclado`: lo que tapa el teclado (la página menos lo que queda a la vista).
 * - `--vista-desplazada`: cuánto ha subido iOS la vista.
 *
 * En Android la página sí encoge con el teclado, así que `--teclado` vale 0 y nada cambia. En la
 * web no se instala. Devuelve cómo desinstalarlo.
 */
export const seguirTecladoEnPantalla = (): (() => void) => {
	const vista = window.visualViewport;
	if (!esApp() || !vista) {
		return () => undefined;
	}

	const estilo = document.documentElement.style;

	let pendiente: number | undefined;

	// Al encoger el diálogo, el campo que se estaba editando puede quedar fuera de lo visible.
	const dejarALaVistaElCampo = () => {
		const enfocado = document.activeElement;
		if (enfocado?.closest(DIALOGO)) {
			enfocado.scrollIntoView({ block: 'center' });
		}
	};

	const actualizar = () => {
		const teclado = Math.max(0, Math.round(window.innerHeight - vista.height));
		estilo.setProperty('--teclado', `${teclado}px`);
		estilo.setProperty('--vista-desplazada', `${Math.round(vista.offsetTop)}px`);

		// Ahora y otra vez cuando el diálogo ha terminado de cambiar de alto: centrarlo solo ahora lo
		// centra en un diálogo que todavía mide lo de antes.
		dejarALaVistaElCampo();
		window.clearTimeout(pendiente);
		pendiente = window.setTimeout(dejarALaVistaElCampo, TRAS_LA_TRANSICION_MS);
	};

	vista.addEventListener('resize', actualizar);
	vista.addEventListener('scroll', actualizar);
	actualizar();

	return () => {
		vista.removeEventListener('resize', actualizar);
		vista.removeEventListener('scroll', actualizar);
		window.clearTimeout(pendiente);
		estilo.removeProperty('--teclado');
		estilo.removeProperty('--vista-desplazada');
	};
};
